import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { Shield, ArrowLeft } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

function PrivacyPage() {
  // Set canonical URL
  useCanonical('https://servio.legal/privacy');
  
  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Helmet>
        <title>Privacy Policy | Servio</title>
        <meta name="description" content="Learn how Servio protects your privacy. Our Privacy Policy explains how we collect, use, and safeguard your information on our process server directory platform." />
        <link rel="canonical" href="https://servio.legal/privacy" />
      </Helmet>
      <Navbar />
      
      <main className="flex-1 py-12 lg:py-16">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors mb-4">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-teal-500/10 border border-teal-500/20">
                  <Shield className="h-6 w-6 text-teal-400" />
                </div>
                <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
              </div>
              <p className="text-slate-400">Last updated: February 2026</p>
            </div>

            {/* Content */}
            <div className="prose prose-invert prose-slate max-w-none space-y-8">
              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">1. Introduction</h2>
                <p className="text-slate-400 leading-relaxed">
                  Servio ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services. Please read this policy carefully. By using Servio, you consent to the practices described in this policy.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-lg font-medium text-white mt-4 mb-2">2.1 Information You Provide</h3>
                <p className="text-slate-400 leading-relaxed">We collect information you voluntarily provide, including:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li><strong className="text-white">Account Information:</strong> Name, email address, password, phone number</li>
                  <li><strong className="text-white">Profile Information:</strong> Business name, bio, address, years of experience, coverage areas, pricing</li>
                  <li><strong className="text-white">Verification Documents:</strong> Licenses, insurance certificates, bonds, and other professional credentials</li>
                  <li><strong className="text-white">Payment Information:</strong> Processed securely through Stripe; we do not store full credit card numbers</li>
                  <li><strong className="text-white">Communications:</strong> Messages sent through our quote request system</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-6 mb-2">2.2 Information Collected Automatically</h3>
                <p className="text-slate-400 leading-relaxed">When you use our Service, we automatically collect:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li><strong className="text-white">Usage Data:</strong> Pages visited, features used, search queries</li>
                  <li><strong className="text-white">Device Information:</strong> Browser type, operating system, IP address</li>
                  <li><strong className="text-white">Analytics Data:</strong> Profile views, quote requests, response times</li>
                </ul>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">3. How We Use Your Information</h2>
                <p className="text-slate-400 leading-relaxed">We use collected information to:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Create and manage your account</li>
                  <li>Process subscriptions and payments</li>
                  <li>Display your profile to potential clients (process servers only)</li>
                  <li>Facilitate quote requests and communications</li>
                  <li>Verify professional credentials and issue verification badges</li>
                  <li>Calculate and display Servio Scores</li>
                  <li>Send transactional emails (quote notifications, account updates)</li>
                  <li>Respond to inquiries and provide support</li>
                  <li>Detect and prevent fraud or abuse</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">4. Information Sharing</h2>
                
                <h3 className="text-lg font-medium text-white mt-4 mb-2">4.1 Public Profile Information</h3>
                <p className="text-slate-400 leading-relaxed">
                  If you are a subscribed process server, certain profile information is publicly visible, including: display name, company name, bio, coverage areas, pricing, verification badges, Servio Score, and contact information you choose to display.
                </p>

                <h3 className="text-lg font-medium text-white mt-6 mb-2">4.2 Third-Party Service Providers</h3>
                <p className="text-slate-400 leading-relaxed">We share information with trusted third parties who assist in operating our Service:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li><strong className="text-white">Stripe:</strong> Payment processing</li>
                  <li><strong className="text-white">Resend:</strong> Email delivery</li>
                  <li><strong className="text-white">MongoDB:</strong> Database hosting</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-6 mb-2">4.3 Legal Requirements</h3>
                <p className="text-slate-400 leading-relaxed">
                  We may disclose your information if required by law, court order, or government request, or if we believe disclosure is necessary to protect rights, safety, or property.
                </p>

                <h3 className="text-lg font-medium text-white mt-6 mb-2">4.4 Business Transfers</h3>
                <p className="text-slate-400 leading-relaxed">
                  In the event of a merger, acquisition, or sale of assets, user information may be transferred to the acquiring entity.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">5. Data Security</h2>
                <p className="text-slate-400 leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your information, including:
                </p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li>Encryption of data in transit (HTTPS/TLS)</li>
                  <li>Secure password hashing</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security assessments</li>
                </ul>
                <p className="text-slate-400 leading-relaxed mt-4">
                  However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">6. Data Retention</h2>
                <p className="text-slate-400 leading-relaxed">
                  We retain your information for as long as your account is active or as needed to provide services. After account deletion:
                </p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li>Profile data is retained for 90 days (for reactivation)</li>
                  <li>Transaction records are retained as required by law</li>
                  <li>Anonymized analytics data may be retained indefinitely</li>
                </ul>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">7. Your Rights and Choices</h2>
                <p className="text-slate-400 leading-relaxed">You have the right to:</p>
                <ul className="text-slate-400 mt-4 space-y-2">
                  <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-white">Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong className="text-white">Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong className="text-white">Portability:</strong> Request your data in a portable format</li>
                  <li><strong className="text-white">Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p className="text-slate-400 leading-relaxed mt-4">
                  To exercise these rights, contact us at the email address below. We will respond within 30 days.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">8. Cookies and Tracking</h2>
                <p className="text-slate-400 leading-relaxed">
                  We use essential cookies to maintain your session and remember your preferences. We do not currently use third-party advertising cookies. You can disable cookies in your browser settings, but some features may not function properly.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">9. Children's Privacy</h2>
                <p className="text-slate-400 leading-relaxed">
                  Servio is not intended for users under 18 years of age. We do not knowingly collect information from children. If we learn we have collected information from a child under 18, we will delete it promptly.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">10. California Privacy Rights</h2>
                <p className="text-slate-400 leading-relaxed">
                  California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, request deletion, and opt-out of the sale of personal information. We do not sell personal information.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">11. Changes to This Policy</h2>
                <p className="text-slate-400 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-white mt-0 mb-4">12. Contact Us</h2>
                <p className="text-slate-400 leading-relaxed">
                  For questions about this Privacy Policy or to exercise your privacy rights, please visit our website for current contact information.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PrivacyPage;
